import {
  ConnectionState,
  PowerLikeRequest_Status,
} from '@project-gd-x/dating-site-contracts/src/gen/gdx/connections/public/v1/dialogs_pb';
import { FeatureType } from '@project-gd-x/dating-site-contracts/src/gen/gdx/market/v1/package_pb';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { computed, Ref } from 'vue';

import { showNotification } from '@/components/notification/helpers/notification-shower/notification-shower';
import { isAllowedCreditsAction } from '@/components/payment/helpers/payment/payment';
import { resolveProgressInPercent } from '@/helpers/number';
import { debounce } from '@/helpers/you-dont-need-lodash/you-dont-need-lodash';
import { ChatStateDialogPreview } from '@/pages/chat/store/chat-state';
import { resolveCreditsLastAction } from '@/pages/payment/helpers/credits-last-action/credits-last-action';
import { pushRoute, router } from '@/router';
import { removeDialogs } from '@/services/api/chat/chat-api';
import { useI18n } from '@/services/i18n';
import { commit, dispatch, getStore } from '@/store/store-helper';
import { ExtractClassFields } from '@/type';

import { messages } from './i18n';

const { t } = useI18n({ messages });

dayjs.extend(duration);
// TODO:  need add api method to request prices. Now is hard code price on frontend.
export const POWER_LIKE_PRICE_CREDITS = 95;
let accumId: string[] = [];
const fetchConnection = debounce(async () => {
  await dispatch('actionProfileGetConnectionState', accumId);
  accumId = [];
}, 500);

export function usePowerLike() {
  let id: string;
  let dialog: ChatStateDialogPreview;

  // Computed ----------------------------------------------------------------------
  const powerLikeDuration = computed<number>(() => {
    const expireDuration = dialog?.powerLike?.expireDuration ?? 0;
    const expiresAt = dialog?.powerLike?.expiresAt ?? 0;

    return resolveProgressInPercent(expireDuration, expiresAt);
  });

  const powerLikeTimeLeftFormatted = computed<string>(() => {
    const expiresAt = dialog?.powerLike?.expiresAt ?? 0;
    const secondsNow = Math.trunc(+new Date() / 1000);
    let timeLeft = expiresAt - secondsNow;

    if (timeLeft < 0) {
      timeLeft = 0;
    }
    return dayjs.duration(timeLeft, 'seconds').format('HH:mm');
  });

  const powerLikePrice = computed<typeof POWER_LIKE_PRICE_CREDITS>(() => {
    return POWER_LIKE_PRICE_CREDITS;
  });

  const isPowerLikeAllowed = computed<boolean>(() => {
    return connection.value?.isPowerLikeAllowed;
  });

  const isLikeAllowed = computed<boolean>(() => {
    return connection.value?.isLikeAllowed;
  });

  const isChatAllowed = computed<boolean>(() => {
    return connection.value?.isChatAllowed;
  });

  const connection = computed<ExtractClassFields<ConnectionState>>(() => {
    return getStore().state.profile.connections[id];
  });

  const powerLike = computed(() => {
    return dialog?.powerLike ?? connection.value?.powerLikeRequest;
  });

  const isAccepted = computed<boolean>(() => {
    return powerLike.value?.status === PowerLikeRequest_Status.ACCEPTED;
  });

  const isActive = computed<boolean>(() => {
    return powerLike.value?.status === PowerLikeRequest_Status.ACTIVE;
  });

  const isDeclined = computed<boolean>(() => {
    return powerLike.value?.status === PowerLikeRequest_Status.DECLINED;
  });

  const isExpired = computed<boolean>(() => {
    return powerLike.value?.status === PowerLikeRequest_Status.EXPIRED;
  });

  const isPowerLikeInProgress = computed<boolean>(() => {
    return isAccepted.value || isActive.value;
  });

  const isPowerLikeRequestInProgress = computed<boolean>(
    () => !!getStore().state.profile.connectionsLoading[id],
  );

  // Method ----------------------------------------------------------------------
  async function cancelPowerLike(): Promise<void> {
    await deleteDialog(dialog?.id ?? '');
    await dispatch('actionProfilePowerLikeCancel', {
      chatId: dialog?.id ?? '',
      userId: dialog?.userId ?? '',
    });
    showNotification({
      type: 'text',
      text: t('declined'),
      icon: 'success',
    });
  }

  async function deleteDialog(id: string): Promise<void> {
    await removeDialogs([id]);

    commit('mutationChatDeleteDialogs', [id]);
    pushRoute('chatList');
  }

  function sendPowerLikeRequest({
    credits,
    localState,
  }: {
    credits: typeof powerLikePrice.value;
    localState?: Ref<boolean>;
  }): void {
    resolveCreditsLastAction('chatRequest', id);

    setTimeout(async () => {
      if (
        await isAllowedCreditsAction(
          credits,
          FeatureType.POWER_LIKE,
          router.currentRoute.value.name as string,
          id,
        )
      ) {
        dispatch('actionProfilePowerLike', id);

        commit('mutationProfileSetPowerLikeRequestStatus', id);

        return;
      }

      if (localState) localState.value = false;
    }, 1000);
  }

  function acceptPowerLike(): void {
    dispatch('actionProfilePowerLikeAccept', {
      chatId: dialog?.id ?? '',
      userId: dialog?.userId ?? '',
    });
    commit('mutationChatSetDialogReadOnly', { dialogId: dialog?.id ?? '', value: false });
  }

  async function loadSingle(idUser: string, dialogState?: ChatStateDialogPreview) {
    id = idUser;
    dialog = dialogState as ChatStateDialogPreview;

    await dispatch('actionProfileGetConnectionState', idUser);
  }

  async function load(idUser: string, dialogState?: ChatStateDialogPreview) {
    id = idUser;
    dialog = dialogState as ChatStateDialogPreview;

    if (!connection.value) {
      accumId.push(id);
      fetchConnection(id);
    }
  }

  return {
    powerLikeDuration,
    powerLikeTimeLeftFormatted,
    powerLikePrice,
    isPowerLikeAllowed,
    isLikeAllowed,
    isChatAllowed,
    connection,
    powerLike,
    isAccepted,
    isActive,
    isDeclined,
    isExpired,
    isPowerLikeInProgress,
    isPowerLikeRequestInProgress,
    cancelPowerLike,
    deleteDialog,
    sendPowerLikeRequest,
    acceptPowerLike,
    loadSingle,
    load,
  };
}
